import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages } from '../coverEditorMessages';
import { OrderList } from './OrderList';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ROLE_NOTES = {
  title: coverEditorMessages.roleTitle,
  artist: coverEditorMessages.roleArtist,
  free: coverEditorMessages.roleFree,
} as const;

const BACKGROUND_NOTES = {
  picture: coverEditorMessages.layerPicture,
  colour: coverEditorMessages.layerColour,
  gradient: coverEditorMessages.layerGradient,
} as const;

/**
 * The Layers panel (design cover-editor d5): everything on the cover from top to bottom: the text
 * boxes (a click selects, the arrows set which box is on top), the effects, and the background.
 */
const LayersPanelComponent: React.FC = () => {
  const intl = useIntl();
  const { state, dispatch, readOnly, setTool } = useCoverEditor();
  const { doc, selection } = state;
  const textRows = useMemo(
    () =>
      doc.texts.map((box) => ({
        id: box.id,
        text: box.text.trim() === '' ? intl.formatMessage(coverEditorMessages.layerEmptyText) : box.text,
        note: intl.formatMessage(ROLE_NOTES[box.role]),
      })),
    [doc.texts, intl]
  );
  const effectRows = useMemo(
    () => doc.effects.map((effect) => ({ id: effect.id, effectType: effect.type })),
    [doc.effects]
  );
  const selectText = useCallback(
    (id: string) => dispatch({ type: 'select', selection: { kind: 'text', id } }),
    [dispatch]
  );
  const selectEffect = useCallback(
    (id: string) => dispatch({ type: 'select', selection: { kind: 'effect', id } }),
    [dispatch]
  );
  const moveText = useCallback(
    (id: string, toIndex: number) => dispatch({ type: 'moveText', id, toIndex }),
    [dispatch]
  );
  const moveEffect = useCallback(
    (id: string, toIndex: number) => dispatch({ type: 'moveEffect', id, toIndex }),
    [dispatch]
  );
  const removeText = useCallback((id: string) => dispatch({ type: 'removeText', id }), [dispatch]);
  const removeEffect = useCallback((id: string) => dispatch({ type: 'removeEffect', id }), [dispatch]);
  const openPicture = useCallback(() => setTool('picture'), [setTool]);

  return (
    <>
      <h3 className={styles.paneTitle}>{intl.formatMessage(coverEditorMessages.toolLayers)}</h3>
      <span className={styles.eyebrow}>{intl.formatMessage(coverEditorMessages.layersText)}</span>
      {textRows.length === 0 ? (
        <span className={styles.hint}>{intl.formatMessage(coverEditorMessages.layersNoText)}</span>
      ) : (
        <OrderList
          rows={textRows}
          selectedId={selection?.kind === 'text' ? selection.id : null}
          disabled={readOnly}
          onSelect={selectText}
          onMove={moveText}
          onRemove={removeText}
        />
      )}
      <span className={styles.eyebrow}>{intl.formatMessage(coverEditorMessages.layersEffects)}</span>
      {effectRows.length === 0 ? (
        <span className={styles.hint}>{intl.formatMessage(coverEditorMessages.layersNoEffects)}</span>
      ) : (
        <OrderList
          rows={effectRows}
          selectedId={selection?.kind === 'effect' ? selection.id : null}
          disabled={readOnly}
          onSelect={selectEffect}
          onMove={moveEffect}
          onRemove={removeEffect}
        />
      )}
      <span className={styles.eyebrow}>{intl.formatMessage(coverEditorMessages.layersBackground)}</span>
      <button type="button" className={styles.layerBackground} onClick={openPicture}>
        {intl.formatMessage(BACKGROUND_NOTES[doc.background.kind])}
      </button>
    </>
  );
};

export const LayersPanel = TypedMemo(LayersPanelComponent);
